/* eslint-disable indent */
import { InputType, InstallType } from 'types/types';

const closeTaskDag = {
  tab: 'Issue Sync',
  description: 'Closed issue in Jira will Close task in Replicon',
  arrowIcon: true,
  dagId: 'close_task',
  customSettings: []
};

const createTaskDag = {
  tab: 'Issue Sync',
  description: 'New issue in Jira will create task in Replicon',
  arrowIcon: true,
  dagId: 'create_task',
  customSettings: []
};

const jiraProjectImportDag = {
  tab: 'Project Sync',
  description: 'New project in Jira will create project in Replicon',
  arrowIcon: true,
  dagId: 'project_import',
  customSettings: []
};

const jiraProjectImportWithSettingsDag = {
  tab: 'Project Sync',
  description: 'New project in Jira will create project in Replicon',
  arrowIcon: true,
  dagId: 'project_import',
  customSettings: [
    {
      id: 'wbsSyncSetting',
      heading: 'Sync Mapping',
      description:
        'Define what Jira issue types you would want to sync to Replicon and how your Jira issues should map to the Replicon project/task structure',
      title: 'What to Sync',
      helperTextValue: '',
      type: InputType.SyncMapping,
      addSettingName: 'Add Mapping',
      dropdownOptions: [
        { Project: 'project' },
        { Task: 'task' },
        { 'Sub-Task Level 1': 'subTaskLevel1' },
        { 'Sub-Task Level 2': 'subTaskLevel2' }
      ],
      isRequired: false
    }
  ]
};

const userSyncCustomSettings = [
  {
    id: 'syncCriteria',
    title: 'User sync criteria',
    helperTextValue:
      'Choose which users from Replicon should be synced to JIRA',
    type: InputType.Dropdown,
    dropdownOptions: [
      { 'All Users': 'ALL_USERS' },
      { 'Based on Roles': 'BASED_ON_ROLES' },
      { 'Based on Groups': 'BASED_ON_GROUPS' }
    ]
  },
  {
    id: 'selectedRoles',
    title: 'Role',
    helperTextValue: 'Enter comma-separated role names (e.g. admin, manager)',
    type: InputType.FilterTextField,
    isRequired: false,
    highlighted: true
  },
  {
    id: 'selectedGroups',
    title: 'Group',
    helperTextValue: 'Enter comma-separated group names',
    type: InputType.FilterTextField,
    isRequired: false,
    highlighted: true
  }
];

const userExportDag = {
  tab: 'User Sync',
  description:
    'Replicon to Jira: New user created in Replicon will add users in Jira',
  arrowIcon: false,
  dagId: 'user_export',
  customSettings: []
};

const userImportDag = {
  tab: 'User Sync',
  description:
    'Jira to Replicon: New user created in Jira will add users in Replicon',
  arrowIcon: true,
  dagId: 'create_user',
  customSettings: userSyncCustomSettings
};

const timeImportDag = {
  tab: 'TimeData Sync',
  description: 'TimeData worklogs in Jira will Sync to Replicon Timesheet.',
  arrowIcon: true,
  dagId: 'time_import',
  customSettings: []
};

export default class DagsSectionConfig {
  dagsSection: any;

  constructor() {
    this.dagsSection = (
      isPsaProduct: boolean,
      installMethod: string | undefined,
      isOobEnabled: boolean
    ) => {
      return {
        bamboohr: [
          {
            tab: 'User',
            description:
              'New employee in BambooHR will create user in Replicon',
            arrowIcon: true,
            dagId: 'user_import',
            customSettings: []
          },
          {
            tab: 'User',
            description:
              'When employee in BambooHR made inactive then user in Replicon will be disabled',
            arrowIcon: true,
            dagId: 'disable_user',
            customSettings: []
          }
        ],
        sagefinancials: [],
        namely: [],
        servicenow: [],
        replicon: [],
        quickbooks: [
          {
            tab: 'Client',
            description:
              'New Customer created in QuickBooks Online will create client in Replicon',
            arrowIcon: true,
            dagId: 'client_import',
            customSettings: []
          },
          {
            tab: 'InvoiceV2',
            description:
              'Sync new invoice in Replicon to QuickBooks Online - V2',
            arrowIcon: false,
            dagId: 'invoice_export',
            customSettings: [
              {
                id: 'createNewCustomerQuickbooks',
                title: 'Create customers in QuickBooks if missing?',
                helperTextValue:
                  'Should we create a new customer in QuickBooks if the Replicon client is not in QuickBooks?',
                type: InputType.Dropdown,
                dropdownOptions: [{ Yes: true }, { No: false }]
              },
              {
                id: 'syncInvoiceWithTaxCode',
                title: 'Enable the tax code in invoice sync?',
                helperTextValue:
                  'Should we sync the invoice to Quickbooks with tax code applied?',
                type: InputType.Dropdown,
                dropdownOptions: [{ No: false }, { Yes: true }] // Kept "No" first because it is the default value and to keep it turned off for existing customers.
              }
            ]
          },
          {
            tab: 'InvoiceV2',
            description:
              'When a invoice is updated in QuickBooks Online, update invoice status in Replicon - V2',
            arrowIcon: true,
            dagId: 'invoice_status_update_import',
            customSettings: []
          }
        ],
        vantagepoint: [
          {
            tab: 'Initial Setup',
            description:
              'Initial configuration and setup for Vantagepoint integration',
            arrowIcon: false,
            dagId: 'initial_setup',
            customSettings: [
              {
                id: 'laborCodeSetting',
                title: '',
                heading: 'Labor Code Configuration',
                description:
                  'Configure the labor code level details before proceeding with the initial setup. Maximum 5 levels allowed.',
                helperTextValue: '',
                type: InputType.LaborCode
              }
            ]
          },
          {
            tab: 'Users',
            description: 'Sync Users from Vantagepoint to Replicon',
            arrowIcon: true,
            dagId: 'user_sync',
            customSettings: [
              {
                id: 'userSyncFilters',
                title: 'User Sync Filters',
                helperTextValue:
                  'Add filters to control which users are synced from Vantagepoint',
                type: InputType.MultipleInputField,
                addSettingName: 'Add Filter',
                placeholder: {
                  field: 'Enter field name',
                  condition: 'Condition',
                  value: 'Enter value'
                },
                conditionOptions: [{ Equals: 'equals' }]
              }
            ]
          },
          {
            tab: 'Projects',
            description: 'Sync Projects from Vantagepoint to Replicon',
            arrowIcon: true,
            dagId: 'project_sync',
            customSettings: [
              {
                id: 'budgetLaborCodeLevel',
                title: 'Budget Labor Code Level',
                heading: 'Employee Assignment Configuration',
                helperTextValue:
                  'Select the labor code level to use for budget sync. "None" disables this feature.',
                type: InputType.Dropdown,
                dropdownOptions: [
                  { None: 'None' },
                  { Task: 'Task' },
                  { TimesheetFields: 'TimesheetFields' }
                ]
              },
              {
                id: 'projectResourceAssignmentLevel',
                title: 'Project Resource Assignment Level',
                helperTextValue:
                  'Select the level at which project resources are assigned. "None" disables this feature.',
                type: InputType.Dropdown,
                dropdownOptions: [
                  { None: 'None' },
                  { 'Project Team Tab\nHub -> Project -> Team': 'Team' },
                  {
                    'Project Plan Tab\nHub -> Project -> Plan -> Labor Code':
                      'Plan'
                  }
                ]
              }
            ]
          },
          {
            tab: 'Timesheets',
            description: 'Sync Timesheets between Replicon and Vantagepoint',
            arrowIcon: false,
            dagId: 'timesheet_sync',
            customSettings: []
          }
        ],
        sageintacct: [],
        salesforce: [
          {
            tab: 'Client',
            description:
              'New or Updated account from Salesforce will Sync as Client in Replicon',
            arrowIcon: true,
            dagId: 'client_import',
            customSettings: [
              {
                id: 'accountTypesToSync',
                title: 'Account types to sync',
                helperTextValue:
                  'Enter a comma separated list of account types to be synced. Put ALL if all account types should be synced.',
                type: InputType.TextField
              },
              {
                id: 'syncAccountsWithNoTypes',
                title: 'Sync accounts with no types?',
                helperTextValue:
                  'Yes if Salesforce accounts with no account type specified should be synced as well. No otherwise.',
                type: InputType.Dropdown,
                dropdownOptions: [{ Yes: true }, { No: false }]
              },
              {
                id: 'toUpdate',
                title: 'To update?',
                helperTextValue:
                  'Yes if updates to Salesforce accounts should be synced as well',
                type: InputType.Dropdown,
                dropdownOptions: [{ Yes: true }, { No: false }]
              }
            ]
          },
          {
            tab: 'Project',
            description:
              'New or Updated opportunities from Salesforce will Sync to Replicon as Projects',
            arrowIcon: true,
            dagId: 'project_import',
            customSettings: [
              {
                id: 'useANDorORLogicalCombination',
                title: 'Use "AND" or "OR" logical combination',
                heading: 'Sync Criteria',
                helperTextValue: '',
                type: InputType.Dropdown,
                dropdownOptions: [{ AND: 'AND' }, { OR: 'OR' }]
              },
              {
                id: 'delimiterCharacter',
                title: 'Delimiter character',
                helperTextValue: '',
                type: InputType.TextField
              },
              {
                id: 'probability',
                title: 'Based on Probability percentage',
                helperTextValue:
                  'Please enter a whole number. For example, entering 75 will sync opportunities that are 75% and above.\nIf left blank this sync criterion will be ignored.',
                type: InputType.TextField
              },
              {
                id: 'basedOnStage',
                title: 'Based on Stage',
                helperTextValue:
                  'Please enter one or more opportunity stages that need to be synced. If you want multiple opportunity stages to be synced, separate them with the selected delimited character as configured above. When you enter stages,only opportunities in those stages will be synced.\nIf left blank or type "All", this sync criterion will be ignored.',
                type: InputType.TextField
              },
              {
                id: 'basedOnType',
                title: 'Based on Type',
                helperTextValue:
                  'Please enter one or more opportunity types that need to be synced. If you want multiple opportunity types to be synced, separate them with the selected delimited character as configured above.Only opportunities in the specified type will sync.\nIf left blank or type "All", this sync criterion will be ignored.\nIf "None" is entered, opportunities that have type "none" assigned will also sync.',
                type: InputType.TextField
              },
              {
                id: 'basedOnOpportunityCustomFields',
                title: 'Based on Opportunity custom field(s)',
                helperTextValue:
                  'Please enter one or more opportunity custom fields that need to be synced. Only picklist type opportunity custom fields are supported.',
                type: InputType.MultipleInputField,
                addSettingName: 'Add Custom Field',
                placeholder: { key: 'Custom Field', value: 'Value' }
              },
              {
                id: 'customFields',
                title: 'Custom Fields',
                heading: 'Opportunity Custom Fields to Sync',
                helperTextValue: 'Use delimiter , etc',
                isRequired: false,
                type: InputType.TextField
              },
              {
                id: 'toUpdate',
                title: 'To update?',
                helperTextValue:
                  'Yes if updates to Salesforce accounts should be synced as well',
                type: InputType.Dropdown,
                dropdownOptions: [{ Yes: true }, { No: false }]
              },
              ...(isPsaProduct
                ? [
                    {
                      id: 'syncAttachmentsFromOpportunity',
                      title: 'Sync Attachments from Opportunity',
                      heading: 'Sync Opportunity Attachment(s)',
                      helperTextValue: '',
                      type: InputType.Dropdown,
                      dropdownOptions: [{ Yes: true }, { No: false }]
                    }
                  ]
                : []),
              {
                id: 'toSyncStatus',
                title: 'Project Status Update',
                heading: 'Project Status',
                description:
                  'Choose the project status by opportunity probability',
                helperTextValue:
                  'Yes if sync the project status based on the probability % value',
                type: InputType.Dropdown,
                dropdownOptions: [{ Yes: true }, { No: false }]
              },
              {
                id: 'statusSyncCriteria',
                type: InputType.RangeCriteriaSelection,
                addSettingName: 'Add Status',
                dropdownOptions: isPsaProduct
                  ? [
                      { Initiate: 'Initiate' },
                      { Planning: 'Planning' },
                      { Execution: 'Execution' },
                      { Closeout: 'Closeout' }
                    ]
                  : [
                      { Tentative: 'Tentative' },
                      { 'In-Progress': 'In Progress' },
                      { Completed: 'Completed' },
                      { Deferred: 'Deferred' },
                      { Cancelled: 'Cancelled' },
                      { Archived: 'Archived' }
                    ]
              }
            ]
          }
        ],
        zendesk: [
          {
            tab: 'Client',
            description:
              'New/Updated Organizations in Zendesk will Create/Update Clients in Replicon',
            arrowIcon: true,
            dagId: 'client_import',
            customSettings: []
          },
          {
            tab: 'Project',
            description:
              'New/Updated Tickets in Zendesk will Create/Update Projects in Replicon',
            arrowIcon: true,
            dagId: 'project_import',
            customSettings: []
          }
        ],
        xero: [
          {
            tab: 'Client',
            description: 'New client in Replicon will Sync to Xero as Contact',
            arrowIcon: false,
            dagId: 'client_export',
            customSettings: []
          },
          {
            tab: 'Client',
            description:
              'New or Updated contact in Xero will Sync to Replicon as Client',
            arrowIcon: true,
            dagId: 'client_import',
            customSettings: []
          },
          {
            tab: 'InvoiceV2',
            description: 'Sync new invoice in Replicon to Xero - V2',
            arrowIcon: false,
            dagId: 'invoice_export',
            customSettings: [
              {
                id: 'createNewContactXero',
                title: 'Create contact in Xero if missing?',
                helperTextValue:
                  'Should we create a new contact in Xero if the Replicon client is not in Xero?',
                type: InputType.Dropdown,
                dropdownOptions: [{ Yes: true }, { No: false }]
              }
            ]
          },
          {
            tab: 'InvoiceV2',
            description: 'Sync Xero "Billed" invoice status to Replicon - V2',
            arrowIcon: true,
            dagId: 'invoice_status_update_billed',
            customSettings: []
          },
          {
            tab: 'InvoiceV2',
            description: 'Sync Xero "Paid" invoice status to Replicon - V2',
            arrowIcon: true,
            dagId: 'invoice_status_update_paid',
            customSettings: []
          }
        ],
        jira:
          installMethod === InstallType.basicAuth
            ? [closeTaskDag, createTaskDag, jiraProjectImportDag, userExportDag]
            : isOobEnabled
            ? [userImportDag, jiraProjectImportWithSettingsDag, timeImportDag]
            : [closeTaskDag, createTaskDag, jiraProjectImportDag],
        computerease: [
          {
            tab: 'Initial Setup',
            description:
              'Configure Replicon tenant based on ComputerEase data (Must run first)',
            arrowIcon: true,
            dagId: 'initial_setup',
            customSettings: []
          },
          {
            tab: 'User',
            description: 'Import users from ComputerEase to Replicon',
            arrowIcon: true,
            dagId: 'user_sync',
            customSettings: [
              {
                id: 'userSyncMappings',
                title: 'User Sync Mappings',
                helperTextValue:
                  'Add filters to control which users are synced from ComputerEase.',
                type: InputType.MultipleInputField,
                addSettingName: 'Add Mapping',
                placeholder: { key: 'Key', value: 'Value' }
              }
            ]
          },
          {
            tab: 'Project',
            description: 'Transfer projects from ComputerEase to Replicon',
            arrowIcon: true,
            dagId: 'project_sync',
            customSettings: []
          },
          {
            tab: 'Timesheet',
            description: 'Import timesheet data from Replicon to ComputerEase',
            arrowIcon: false,
            dagId: 'time_sync',
            customSettings: []
          }
        ]
      };
    };
  }

  getDagsSection(
    shortName: string,
    isPsaProduct: boolean,
    installMethod: string | undefined,
    isOobEnabled?: boolean
  ) {
    return this.dagsSection(isPsaProduct, installMethod, isOobEnabled ?? false)[
      shortName
    ];
  }
}
